const _jsxFileName = '';
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const ResetPassword = ({ onNavigate, token: propToken }) => {
  const [token, setToken] = useState(() => {
    if (propToken) return propToken;
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setMessage('Invalid reset link');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setMessage('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setMessage('Password updated successfully! Redirecting to login...');
        setTimeout(() => onNavigate('auth'), 3000);
      } else {
        setMessage(data.error || data.message || 'Failed to reset password');
      }
    } catch (error) {
      setMessage('Connection error');
    } finally {
      setIsLoading(false);
    }
  };

  return React.createElement(
    'div',
    {
      className: 'min-h-screen flex items-center justify-center bg-stone-50 p-6',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 68 },
    },
    React.createElement(
      'div',
      {
        className: 'bg-white w-full max-w-md p-8 shadow-xl',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 69 },
      },
      React.createElement(
        'button',
        {
          onClick: () => onNavigate('auth'),
          className:
            'flex items-center gap-2 text-stone-400 hover:text-stone-900 text-xs uppercase tracking-widest mb-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 70 },
        },

        React.createElement(ArrowLeft, {
          size: 14,
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 74 },
        }),
        ' Back to Login'
      ),

      React.createElement(
        'h1',
        {
          className: 'font-serif text-3xl text-stone-900 mb-2',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 77 },
        },
        'Reset Password'
      ),
      React.createElement(
        'p',
        {
          className: 'text-stone-400 text-sm mb-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 78 },
        },
        'Enter your new password below'
      ),

      message &&
        React.createElement(
          'div',
          {
            className: `p-3 rounded flex items-center gap-3 text-sm mb-6 ${
              success ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
            }`,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 81 },
          },
          success
            ? React.createElement(Check, {
                size: 16,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 84 },
              })
            : React.createElement(AlertCircle, {
                size: 16,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 84 },
              }),
          React.createElement(
            'span',
            { __self: this, __source: { fileName: _jsxFileName, lineNumber: 85 } },
            message
          )
        ),

      React.createElement(
        'form',
        {
          onSubmit: handleSubmit,
          className: 'space-y-6',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 89 },
        },
        React.createElement(
          'div',
          {
            className: 'group relative',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 90 },
          },
          React.createElement('input', {
            type: showPassword ? 'text' : 'password',
            value: password,
            onChange: (e) => setPassword(e.target.value),
            required: true,
            className:
              'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent pr-8',
            placeholder: 'New Password',
            id: 'new-password',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 91 },
          }),
          React.createElement(
            'label',
            {
              htmlFor: 'new-password',
              className:
                'absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 100 },
            },
            'New Password'
          ),
          React.createElement(
            'button',
            {
              type: 'button',
              onClick: () => setShowPassword(!showPassword),
              className: 'absolute right-0 top-3 text-stone-400 hover:text-stone-600',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 103 },
            },

            showPassword
              ? React.createElement(EyeOff, {
                  size: 18,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 108 },
                })
              : React.createElement(Eye, {
                  size: 18,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 108 },
                })
          )
        ),

        React.createElement(
          'div',
          {
            className: 'group relative',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 112 },
          },
          React.createElement('input', {
            type: showPassword ? 'text' : 'password',
            value: confirmPassword,
            onChange: (e) => setConfirmPassword(e.target.value),
            required: true,
            className:
              'w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 focus:outline-none focus:border-gold-600 transition-colors peer placeholder-transparent',
            placeholder: 'Confirm Password',
            id: 'confirm-password',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 113 },
          }),
          React.createElement(
            'label',
            {
              htmlFor: 'confirm-password',
              className:
                'absolute left-0 -top-3.5 text-xs text-gold-600 transition-all peer-placeholder-shown:text-base peer-placeholder-shown:text-stone-400 peer-placeholder-shown:top-3 peer-focus:-top-3.5 peer-focus:text-gold-600 peer-focus:text-xs',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 122 },
            },
            'Confirm Password'
          )
        ),

        React.createElement(
          'button',
          {
            type: 'submit',
            disabled: isLoading || !token,
            className:
              'w-full bg-stone-900 text-white py-4 uppercase text-xs tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-50 mt-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 127 },
          },

          isLoading ? 'Updating...' : 'Update Password'
        )
      )
    )
  );
};

export default ResetPassword;
